import { type ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '../tokens';
import Button from '@mui/material/Button';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export interface NavItem {
  id: string;
  label: string;
  icon: ReactNode;
}

export interface NavSection {
  title: string;
  items: NavItem[];
  collapsible?: boolean;
}

export interface NavigationSidebarProps {
  sections: NavSection[];
  activeItemId?: string;
  onItemClick: (id: string) => void;
}

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: 220,
      flexShrink: 0,
      padding: '16px 0',
      background: isLight ? '#F5F5F5' : '#1E1E1E',
      borderRight: `1px solid ${c.border.default}`,
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 16px',
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.95rem',
      fontWeight: 600,
      color: c.text.primary,
      userSelect: 'none',
    },
    chevron: {
      width: 20,
      height: 20,
      color: c.text.secondary,
    },
    navItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      width: '100%',
      padding: '10px 16px',
      border: 'none',
      borderRadius: 8,
      background: 'transparent',
      cursor: 'pointer',
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.85rem',
      fontWeight: 400,
      color: c.text.secondary,
      textAlign: 'left',
      textTransform: 'none',
      justifyContent: 'flex-start',
      transition: 'background 0.15s ease, color 0.15s ease',
      '&:hover': {
        background: isLight
          ? 'rgba(0, 0, 0, 0.04)'
          : 'rgba(255, 255, 255, 0.06)',
        color: c.text.primary,
      },
      '& svg': {
        width: 20,
        height: 20,
        flexShrink: 0,
      },
    },
    navItemActive: {
      color: c.primary.main,
      fontWeight: 600,
      background: isLight
        ? 'rgba(0, 120, 214, 0.08)'
        : 'rgba(0, 120, 214, 0.15)',
      '&:hover': {
        background: isLight
          ? 'rgba(0, 120, 214, 0.12)'
          : 'rgba(0, 120, 214, 0.2)',
        color: c.primary.main,
      },
    },
  };
});

export const NavigationSidebar = ({
  sections,
  activeItemId,
  onItemClick,
}: Readonly<NavigationSidebarProps>) => {
  const { classes } = useStyles();

  return (
    <nav className={classes.root}>
      {sections.map(section => (
        <div key={section.title}>
          <div className={classes.sectionTitle}>
            {section.title}
            {section.collapsible && (
              <KeyboardArrowDownIcon className={classes.chevron} />
            )}
          </div>
          {section.items.map(item => (
            <Button
              key={item.id}
              className={`${classes.navItem} ${item.id === activeItemId ? classes.navItemActive : ''}`}
              onClick={() => onItemClick(item.id)}
            >
              {item.icon}
              {item.label}
            </Button>
          ))}
        </div>
      ))}
    </nav>
  );
};
